import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type {
  ApiKeyList,
  ApiKeyRow,
  CreateApiKeyResponse,
  DeclaredTool,
  ExclusionLists,
  PricingModel,
  PutExclusionsRequest,
  RevocationReason,
  RevokeApiKeyResponse,
  SiteDetail,
  SlotList,
  SlotRow,
  TargetingCategory,
} from "../../api/types";
import { CommandError } from "../../components/CommandError";
import { CursorListView } from "../../components/CursorListView";
import { DomainVerification } from "../../components/DomainVerification";
import {
  Banner,
  Button,
  ButtonLink,
  Card,
  Checkbox,
  CopyField,
  Dialog,
  EmptyState,
  ErrorState,
  Input,
  NotAvailable,
  PageHeader,
  QueryView,
  RadioGroup,
  Select,
  SkeletonBlock,
  StatusPill,
  Table,
  Textarea,
} from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { categoryLabel, useTargetingCategories } from "../../lib/targetingCategories";
import { useCursorList } from "../../lib/useCursorList";
import { useIntentKey } from "../../lib/useIntentKey";
import { BLOCKS, REASON_CATEGORY, remediationText, SERVING_BLOCKED_REASON, servingUsesText, SITE_STATUS, SLOT_STATUS, TOOL_REVIEW_STATUS } from "./siteCopy";

// UX-DESIGN.md §3.1 (status, verification, suspension, declared tools) and
// §3.2 (slots, API keys, exclusions), migrations 0057 and 0066:
//   GET  /v1/sites/{id}                 effective status, suspension, domain control, tool_review (G-32/G-33)
//   GET|POST .../domain-verification    DNS challenge; verify activates the site
//   GET|PUT /v1/sites/{id}/declared-tools   the tool set that gates serving (G-33)
//   GET  /v1/sites/{id}/slots           slot list (G-34); POST creates one
//   POST /v1/slots/{id}/versions, .../withdraw, .../pause|resume|retire   slot lifecycle (G-34)
//   GET|PUT /v1/sites/{id}/exclusions, /v1/slots/{id}/exclusions   brand-safety exclusions (G-36)
//   GET  /v1/sites/{id}/integration     SDK integration health (G-37)
//   GET  /v1/sites/{id}/api-keys        key list (G-35); POST creates one (secret once)
//   POST /v1/api-keys/{id}/revoke       {reason: rotation | compromised}
// Test keys and key scopes still need endpoints (G-35). GET
// /v1/targeting/categories is publisher-readable too (a web-reported fix
// alongside 0068's cursor round-trip), so a declared tool's category and
// an exclusion list's categories are pickers backed by it
// (lib/targetingCategories.ts), falling back to the old free-text field
// if the fetch fails.

export function SiteDetailPage() {
  const { siteId = "" } = useParams();
  const [params] = useSearchParams();
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["site", siteId], queryFn: () => ep.publisher.getSite(siteId) });
  const site = q.data;

  return (
    <div className="stack-lg">
      <PageHeader
        title={site?.domain ?? "Site"}
        back={{ to: "/publisher/sites", label: "Sites" }}
        description={
          <>
            Site ID <code>{siteId}</code>
          </>
        }
        actions={
          <>
            <ButtonLink to={`/publisher/sites/${siteId}/integration`}>SDK integration</ButtonLink>
            <ButtonLink to={`/publisher/earnings?site=${siteId}`}>Earnings</ButtonLink>
          </>
        }
      />
      {params.get("new") === "1" ? (
        <Banner tone="success" title="Site added" live="polite">
          Next, publish the DNS record below to prove you control the domain.
        </Banner>
      ) : null}
      {q.isPending ? <SkeletonBlock label="Loading site" lines={4} /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {site ? (
        <>
          <StatusCard site={site} />
          <Card title="Domain control" subtitle="A DNS TXT record proves you control this domain.">
            <DomainVerification
              subject="site"
              queryKey={["site-domain", siteId]}
              load={() => ep.publisher.getSiteDomainChallenge(siteId)}
              verify={() => ep.publisher.verifySiteDomain(siteId)}
              onVerified={() => void q.refetch()}
            />
            <p className="xs muted" style={{ marginTop: "var(--space-3)" }}>
              {site.domain_control.last_attempt
                ? `Last checked ${formatLocal(site.domain_control.last_attempt.at)}: ${site.domain_control.last_attempt.verified ? "record found" : "record not found"}. `
                : ""}
              The record doesn't expire. Sites aren't re-verified on a schedule yet.
            </p>
          </Card>
          <DeclaredToolsCard site={site} onSaved={() => void q.refetch()} />
          <SlotsCard site={site} />
          <ExclusionsCard scope="site" id={site.site_id} title="Brand-safety exclusions" subtitle="Applies to every slot on this site, together with each slot's own list." />
          <ApiKeysCard site={site} />
        </>
      ) : null}
    </div>
  );
}

function StatusCard({ site }: { site: SiteDetail }) {
  const s = SITE_STATUS[site.effective_status];
  const sus = site.suspension;
  return (
    <Card title="Status">
      <div className="stack">
        <div className="row">
          <StatusPill tone={s?.tone ?? "neutral"}>{s?.label ?? site.effective_status}</StatusPill>
          <span className="small muted">
            {site.slot_count} {site.slot_count === 1 ? "slot" : "slots"} · {site.live_api_key_count} live{" "}
            {site.live_api_key_count === 1 ? "key" : "keys"}
          </span>
        </div>
        <p>{s?.detail}</p>
        {site.tool_review ? (
          <Banner
            tone={site.tool_review.serving_uses === "none" ? "warning" : site.tool_review.status === "rejected" ? "error" : "neutral"}
            title={`Tool declaration: ${TOOL_REVIEW_STATUS[site.tool_review.status]?.label ?? site.tool_review.status}`}
          >
            {servingUsesText(site.tool_review)}
          </Banner>
        ) : null}
        {sus ? (
          <Banner tone={sus.kind === "safety" ? "error" : "warning"} title={sus.kind === "safety" ? "Safety suspension" : "Ordinary suspension"}>
            <div className="stack-sm">
              <span>
                Reason: <strong>{REASON_CATEGORY[sus.reason_category] ?? sus.reason_category}</strong>, since {formatLocal(sus.since)}.
              </span>
              <span>Stopped: {sus.blocks.map((b) => BLOCKS[b] ?? b).join(", ")}.</span>
              <span>
                {sus.kind === "safety"
                  ? "Not permitted during a safety suspension: paid ads and house ads."
                  : "Still permitted: CPM serving, house ads, and capturing and paying out what's already earned."}
              </span>
              <span>
                Affected slots: {sus.affected_slot_ids.length ? `all ${sus.affected_slot_ids.length} on this site` : "none (no slots yet)"}.
              </span>
              <span>{remediationText(sus)}</span>
            </div>
          </Banner>
        ) : null}
      </div>
    </Card>
  );
}

const emptyTool = (): DeclaredTool => ({ name: "", description: "", category: null });

/**
 * G-33: declare the site's MCP tool set, which gates serving alongside
 * domain verification. A new declaration supersedes any pending one and
 * starts review; resubmitting the approved set withdraws a pending change.
 */
function DeclaredToolsCard({ site, onSaved }: { site: SiteDetail; onSaved: () => void }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const categories = useTargetingCategories();
  const q = useQuery({ queryKey: ["declared-tools", site.site_id], queryFn: () => ep.publisher.getDeclaredTools(site.site_id) });
  const review = q.data?.tool_review ?? site.tool_review;
  const [editing, setEditing] = useState(false);
  const [tools, setTools] = useState<DeclaredTool[]>([emptyTool()]);
  const [locale, setLocale] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: (req: { tools: DeclaredTool[]; locale: string | undefined; sig: string }) =>
      ep.publisher.putDeclaredTools(
        site.site_id,
        { tools: req.tools, ...(req.locale ? { locale: req.locale } : {}) },
        { idempotencyKey: intent.keyFor(req.sig) },
      ),
    onSuccess: () => {
      intent.reset();
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["declared-tools", site.site_id] });
      onSaved();
    },
  });

  function startEdit() {
    const current = review?.approved?.tools ?? review?.pending?.tools ?? [];
    setTools(current.length ? current.map((t) => ({ ...t })) : [emptyTool()]);
    setLocale(review?.approved?.locale ?? site.locale ?? "");
    m.reset();
    setFormError(null);
    setEditing(true);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const cleaned = tools.map((t) => ({ name: t.name.trim(), description: t.description.trim(), category: t.category?.trim() || null }));
    if (!cleaned.length || cleaned.some((t) => !t.name || !t.description)) {
      setFormError("Every tool needs a name and a description.");
      return;
    }
    const names = new Set<string>();
    for (const t of cleaned) {
      if (names.has(t.name)) {
        setFormError(`Tool name "${t.name}" is declared twice.`);
        return;
      }
      names.add(t.name);
    }
    if (cleaned.length > 50) {
      setFormError("Declare at most 50 tools.");
      return;
    }
    setFormError(null);
    const trimmedLocale = locale.trim() || undefined;
    m.mutate({ tools: cleaned, locale: trimmedLocale, sig: JSON.stringify({ tools: cleaned, locale: trimmedLocale }) });
  }

  return (
    <Card title="Declared tools" subtitle="Every MCP tool a slot can carry an ad for must be declared and approved before it serves paid ads.">
      <div className="stack">
        {review ? (
          <div className="stack-sm">
            <div className="row">
              <StatusPill tone={TOOL_REVIEW_STATUS[review.status]?.tone ?? "neutral"}>{TOOL_REVIEW_STATUS[review.status]?.label ?? review.status}</StatusPill>
              {review.approved ? <span className="small muted">Approved set: version {review.approved.version_no}</span> : null}
            </div>
            {review.approved ? (
              <ToolList tools={review.approved.tools} categories={categories.data?.categories} />
            ) : (
              <p className="muted small">No approved tool set yet.</p>
            )}
            {review.pending ? (
              <Banner tone="info" title={`Pending review: version ${review.pending.version_no}`}>
                <ToolList tools={review.pending.tools} categories={categories.data?.categories} />
              </Banner>
            ) : null}
            {review.rejected_since_approved ? (
              <Banner tone="warning" title="A later submission was rejected">
                {review.rejected_since_approved.review_reason ?? "No reason recorded."}
              </Banner>
            ) : null}
          </div>
        ) : null}
        {m.isError && !editing ? <CommandError error={m.error} /> : null}
        {m.data && !editing ? (
          <Banner tone={m.data.applied ? "success" : "info"} live="polite" title={m.data.applied ? "Submitted for review" : "Unchanged"}>
            {m.data.applied
              ? "A reviewer will approve or reject it; the approved set keeps serving until then."
              : "This is already the approved or pending set."}
          </Banner>
        ) : null}
        {!editing ? (
          <div>
            <Button variant="secondary" onClick={() => startEdit()}>
              {review?.approved || review?.pending ? "Declare a change" : "Declare tools"}
            </Button>
          </div>
        ) : (
          <form className="stack" onSubmit={submit} noValidate>
            <h3 className="small">Tools</h3>
            {tools.map((t, i) => (
              <div key={i} className="stack-sm" style={{ borderTop: i ? "1px solid var(--border)" : undefined, paddingTop: i ? "var(--space-2)" : undefined }}>
                <div className="row" style={{ gap: "var(--space-2)" }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      label="Tool name"
                      mono
                      value={t.name}
                      onChange={(e) => setTools((xs) => xs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    {categories.isError ? (
                      <Input
                        label="Category"
                        optional
                        value={t.category ?? ""}
                        hint="A GET /v1/targeting/categories key, e.g. finance. Leave empty for a general-purpose tool. (Couldn't load the category list, so this is free text.)"
                        onChange={(e) => setTools((xs) => xs.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)))}
                      />
                    ) : (
                      <Select
                        label="Category"
                        optional
                        value={t.category ?? ""}
                        disabled={!categories.data}
                        hint={categories.data ? "The category the publisher's tool call is about." : "Loading the category list…"}
                        options={
                          categories.data
                            ? [{ value: "", label: "General-purpose (no category)" }, ...categories.data.categories.map((c) => ({ value: c.key, label: c.label }))]
                            : [{ value: t.category ?? "", label: t.category ?? "General-purpose (no category)" }]
                        }
                        onChange={(e) => setTools((xs) => xs.map((x, j) => (j === i ? { ...x, category: e.target.value || null } : x)))}
                      />
                    )}
                  </div>
                  {tools.length > 1 ? (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setTools((xs) => xs.filter((_, j) => j !== i))}>
                      Remove
                    </Button>
                  ) : null}
                </div>
                <Textarea
                  label="Description"
                  rows={2}
                  value={t.description}
                  onChange={(e) => setTools((xs) => xs.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                />
              </div>
            ))}
            <div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setTools((xs) => [...xs, emptyTool()])}>
                Add another tool
              </Button>
            </div>
            <Input
              label="Locale"
              optional
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              hint="BCP 47, like en, en-US or zh-Hant-TW. Stored at once; not reviewed."
            />
            {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
            {m.isError ? <CommandError error={m.error} /> : null}
            <div className="row">
              <Button type="submit" loading={m.isPending}>
                Submit for review
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditing(false)} disabled={m.isPending}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}

function ToolList({ tools, categories }: { tools: DeclaredTool[]; categories?: TargetingCategory[] }) {
  if (!tools.length) return <p className="muted small">No tools.</p>;
  return (
    <ul className="stack-sm">
      {tools.map((t) => (
        <li key={t.name}>
          <code>{t.name}</code>
          {t.category ? <span className="small muted"> · {categoryLabel(categories, t.category)}</span> : null} — {t.description}
        </li>
      ))}
    </ul>
  );
}

/** G-36: brand-safety exclusions, shared by the site scope and (from a dialog) each slot. */
function ExclusionsCard({ scope, id, title, subtitle }: { scope: "site" | "slot"; id: string; title: string; subtitle?: string }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const queryKey = [scope === "site" ? "site-exclusions" : "slot-exclusions", id];
  const q = useQuery<ExclusionLists | (ExclusionLists & { site_exclusions: ExclusionLists })>({
    queryKey,
    queryFn: () => (scope === "site" ? ep.publisher.getSiteExclusions(id) : ep.publisher.getSlotExclusions(id)),
  });
  const categories = useTargetingCategories();
  const [editing, setEditing] = useState(false);
  const [advertiserIds, setAdvertiserIds] = useState("");
  const [domains, setDomains] = useState("");
  const [excludedCategories, setExcludedCategories] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: (req: PutExclusionsRequest & { sig: string }) =>
      scope === "site"
        ? ep.publisher.putSiteExclusions(id, req, { idempotencyKey: intent.keyFor(req.sig) })
        : ep.publisher.putSlotExclusions(id, req, { idempotencyKey: intent.keyFor(req.sig) }),
    onSuccess: () => {
      intent.reset();
      setEditing(false);
      void qc.invalidateQueries({ queryKey });
    },
  });

  function startEdit(current: ExclusionLists) {
    setAdvertiserIds(current.advertiser_ids.join(", "));
    setDomains(current.destination_domains.join(", "));
    setExcludedCategories(current.categories);
    m.reset();
    setFormError(null);
    setEditing(true);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const ids = splitList(advertiserIds);
    for (const v of ids) {
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) {
        setFormError(`"${v}" isn't a valid advertiser id (a UUID).`);
        return;
      }
    }
    setFormError(null);
    const req = { advertiser_ids: ids, destination_domains: splitList(domains).map((d) => d.toLowerCase()), categories: excludedCategories };
    m.mutate({ ...req, sig: JSON.stringify(req) });
  }

  return (
    <Card title={title} subtitle={subtitle}>
      <QueryView query={q} loadingLabel="Loading exclusions">
        {(ex) => (
          <div className="stack">
            {!editing ? (
              <>
                <dl className="definition-list">
                  <dt>Excluded advertisers</dt>
                  <dd className="mono">{ex.advertiser_ids.length ? ex.advertiser_ids.join(", ") : "None"}</dd>
                  <dt>Excluded destination domains</dt>
                  <dd>{ex.destination_domains.length ? ex.destination_domains.join(", ") : "None"}</dd>
                  <dt>Excluded categories</dt>
                  <dd>{ex.categories.length ? ex.categories.map((k) => categoryLabel(categories.data?.categories, k)).join(", ") : "None"}</dd>
                  {ex.updated_at ? (
                    <>
                      <dt>Last updated</dt>
                      <dd>{formatLocal(ex.updated_at)}</dd>
                    </>
                  ) : null}
                </dl>
                {"site_exclusions" in ex ? (
                  <p className="small muted">
                    Plus the site's own exclusions: {ex.site_exclusions.advertiser_ids.length + ex.site_exclusions.destination_domains.length + ex.site_exclusions.categories.length
                      ? "set — applies on top of this slot's list."
                      : "none set."}
                  </p>
                ) : null}
                {m.data ? <Banner tone="success" live="polite" title="Saved" /> : null}
                <div>
                  <Button variant="secondary" onClick={() => startEdit(ex)}>
                    Edit
                  </Button>
                </div>
              </>
            ) : (
              <form className="stack" onSubmit={submit} noValidate>
                <Textarea
                  label="Excluded advertiser ids"
                  optional
                  rows={2}
                  value={advertiserIds}
                  onChange={(e) => setAdvertiserIds(e.target.value)}
                  hint="Comma-separated advertiser ids (UUIDs). There's no advertiser picker yet."
                />
                <Textarea
                  label="Excluded destination domains"
                  optional
                  rows={2}
                  value={domains}
                  onChange={(e) => setDomains(e.target.value)}
                  hint="Comma-separated domains, e.g. example.com. Covers their subdomains."
                />
                <CategoryExclusionPicker value={excludedCategories} onChange={setExcludedCategories} categories={categories} />
                {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
                {m.isError ? <CommandError error={m.error} /> : null}
                <div className="row">
                  <Button type="submit" loading={m.isPending}>
                    Save exclusions
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)} disabled={m.isPending}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}
      </QueryView>
    </Card>
  );
}

/**
 * The exclusion editor's category list: a checkbox picker backed by GET
 * /v1/targeting/categories (all categories are excludable — unlike
 * campaignInputs.tsx's advertiser-side CategoryPicker, exclusion isn't
 * gated by whether a category is cleared for targeting). Falls back to
 * the original comma-separated free text if the fetch fails.
 */
function CategoryExclusionPicker({
  value,
  onChange,
  categories,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  categories: ReturnType<typeof useTargetingCategories>;
}) {
  // The fallback's own text buffer: splitting `value` into an array and
  // rejoining it with ", " on every keystroke would eat the comma/space
  // the user is in the middle of typing (splitList drops empty tokens),
  // so the raw text is tracked separately and only turned into the array
  // that's actually submitted.
  const [fallbackText, setFallbackText] = useState(() => value.join(", "));
  if (categories.isError) {
    return (
      <Textarea
        label="Excluded categories"
        optional
        rows={2}
        value={fallbackText}
        onChange={(e) => {
          setFallbackText(e.target.value);
          onChange(splitList(e.target.value));
        }}
        hint="Comma-separated GET /v1/targeting/categories keys. (Couldn't load the category list, so this is free text.)"
      />
    );
  }
  if (!categories.data) {
    return <SkeletonBlock label="Loading the category list" lines={2} />;
  }
  return (
    <fieldset className="radio-group">
      <legend>Excluded categories</legend>
      <p className="small muted">Ads targeting any of these categories won't serve here.</p>
      <div className="stack-sm">
        {categories.data.categories.map((c) => (
          <Checkbox
            key={c.key}
            label={c.label}
            checked={value.includes(c.key)}
            onChange={(e) => onChange(e.target.checked ? [...value, c.key] : value.filter((k) => k !== c.key))}
          />
        ))}
      </div>
      <p className="small" role="status">
        {value.length === 0 ? "No categories excluded." : `Excluded: ${value.map((k) => categoryLabel(categories.data!.categories, k)).join(", ")}.`}
      </p>
    </fieldset>
  );
}

function splitList(s: string): string[] {
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function SlotsCard({ site }: { site: SiteDetail }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const list = useCursorList<SlotList, SlotRow>(["slots", site.site_id], (cursor) => ep.publisher.listSlots(site.site_id, cursor), (p) => p.slots);
  const [tool, setTool] = useState("");
  const [model, setModel] = useState<PricingModel | "">("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [managing, setManaging] = useState<SlotRow | null>(null);

  function refreshSlots() {
    void qc.invalidateQueries({ queryKey: ["slots", site.site_id] });
    void qc.invalidateQueries({ queryKey: ["site", site.site_id] });
  }

  const m = useMutation({
    mutationFn: (req: { tool_name: string; pricing_model: PricingModel }) =>
      ep.publisher.createSlot(site.site_id, req, { idempotencyKey: intent.keyFor(JSON.stringify(req)) }),
    onSuccess: () => {
      intent.reset();
      setTool("");
      setModel("");
      refreshSlots();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const fe: Record<string, string> = {};
    if (!tool.trim()) fe.tool = "Enter the MCP tool name, exactly as your server exposes it.";
    if (!model) fe.model = "Choose a pricing model.";
    setErrors(fe);
    if (!Object.keys(fe).length) m.mutate({ tool_name: tool.trim(), pricing_model: model as PricingModel });
  }

  return (
    <Card title="Slots" subtitle="A slot is one MCP tool whose responses can carry an ad. Its pricing model is fixed; changing it creates a new reviewed version.">
      <div className="stack">
        <CursorListView list={list} loadingLabel="Loading slots" empty={<EmptyState icon="list" title="No slots yet">Add one below for each tool that can carry an ad.</EmptyState>}>
          {(rows) => (
            <Table
              caption="Slots on this site"
              captionHidden
              rows={rows}
              rowKey={(s) => s.slot_id}
              columns={[
                { key: "tool", header: "Tool", cell: (s) => <code>{s.tool_name}</code> },
                { key: "model", header: "Pricing", cell: (s) => (s.live_version ? s.live_version.pricing_model.toUpperCase() : "—") },
                {
                  key: "status",
                  header: "Status",
                  cell: (s) => {
                    const st = SLOT_STATUS[s.status];
                    return <StatusPill tone={st?.tone ?? "neutral"}>{st?.label ?? s.status}</StatusPill>;
                  },
                },
                {
                  key: "pending",
                  header: "Pending review",
                  cell: (s) =>
                    s.pending_version
                      ? `${s.pending_version.pricing_model.toUpperCase()} (version ${s.pending_version.version_no})${s.pending_version.review_required === false ? " — live at once" : ""}`
                      : "—",
                },
                {
                  key: "blocked",
                  header: "Why blocked",
                  cell: (s) => (s.serving_blocked_reason ? SERVING_BLOCKED_REASON[s.serving_blocked_reason] ?? s.serving_blocked_reason : "—"),
                },
                { key: "added", header: "Added", cell: (s) => formatLocal(s.created_at) },
                {
                  key: "act",
                  header: "Actions",
                  cell: (s) => (
                    <Button size="sm" variant="secondary" onClick={() => setManaging(s)}>
                      Manage
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </CursorListView>
        {managing ? <ManageSlotDialog slot={managing} onClose={() => setManaging(null)} onChanged={refreshSlots} /> : null}
        <form className="stack" onSubmit={submit} noValidate>
          <h3>Add a slot</h3>
          <div style={{ maxWidth: 480 }}>
            <Input label="Tool name" mono value={tool} onChange={(e) => setTool(e.target.value)} error={errors.tool} />
          </div>
          <RadioGroup<PricingModel>
            legend="Pricing model"
            name="slot_pricing"
            value={model}
            onChange={setModel}
            options={[
              { value: "cpm", label: "CPM", hint: "Paid per thousand acknowledged impressions. Needs the post-send hook and a sampling channel." },
              { value: "cpe", label: "CPE", hint: "Paid per engagement. While CPE billing is in evaluation mode, CPE activity is recorded but not paid." },
            ]}
          />
          {errors.model ? <div className="field-error" role="alert">{errors.model}</div> : null}
          {m.isError ? <CommandError error={m.error} /> : null}
          <div>
            <Button type="submit" loading={m.isPending}>
              Add slot
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
}

/** G-34: a slot's lifecycle — a new (reviewed, if pricing changes) version, withdraw, pause/resume/retire — plus its own exclusions (G-36). */
function ManageSlotDialog({ slot, onClose, onChanged }: { slot: SlotRow; onClose: () => void; onChanged: () => void }) {
  const ep = useEndpoints();
  const intent = useIntentKey();
  const [newModel, setNewModel] = useState<PricingModel | "">("");
  const [newTool, setNewTool] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const versionCmd = useMutation({
    mutationFn: (req: { pricing_model?: PricingModel; tool_name?: string; sig: string }) =>
      ep.publisher.createSlotVersion(slot.slot_id, req, { idempotencyKey: intent.keyFor(req.sig) }),
    onSuccess: () => {
      intent.reset();
      setNewModel("");
      setNewTool("");
      onChanged();
    },
  });
  const withdrawCmd = useMutation({
    mutationFn: () => ep.publisher.withdrawSlotVersion(slot.slot_id, slot.pending_version!.slot_version_id),
    onSuccess: onChanged,
  });
  const statusCmd = useMutation({
    mutationFn: (action: "pause" | "resume" | "retire") =>
      action === "pause" ? ep.publisher.pauseSlot(slot.slot_id) : action === "resume" ? ep.publisher.resumeSlot(slot.slot_id) : ep.publisher.retireSlot(slot.slot_id),
    onSuccess: onChanged,
  });

  function submitVersion(e: FormEvent) {
    e.preventDefault();
    if (!newModel && !newTool.trim()) {
      setFormError("Choose a new pricing model, a new tool name, or both.");
      return;
    }
    setFormError(null);
    const req = { ...(newModel ? { pricing_model: newModel as PricingModel } : {}), ...(newTool.trim() ? { tool_name: newTool.trim() } : {}) };
    versionCmd.mutate({ ...req, sig: JSON.stringify(req) });
  }

  // The dialog opened with a snapshot of the slot row; a status change's own
  // response is the freshest source for what to show until the list
  // refetches and this dialog is reopened, so it wins when present.
  const effectiveStatus = statusCmd.data?.status ?? slot.status;
  const retired = effectiveStatus === "retired";

  return (
    <Dialog open onClose={onClose} title={`Manage ${slot.tool_name}`} description={`Slot ${slot.slot_id}`} actions={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      <div className="stack">
        <dl className="definition-list">
          <dt>Status</dt>
          <dd>
            <StatusPill tone={SLOT_STATUS[effectiveStatus]?.tone ?? "neutral"}>{SLOT_STATUS[effectiveStatus]?.label ?? effectiveStatus}</StatusPill>
          </dd>
          <dt>Live version</dt>
          <dd>{slot.live_version ? `${slot.live_version.pricing_model.toUpperCase()} · version ${slot.live_version.version_no}` : "None"}</dd>
          {slot.pending_version ? (
            <>
              <dt>Pending version</dt>
              <dd>
                {slot.pending_version.pricing_model.toUpperCase()} · version {slot.pending_version.version_no}
                {slot.pending_version.review_required === false ? " (no review needed)" : " (awaiting review)"}
              </dd>
            </>
          ) : null}
          {slot.serving_blocked_reason ? (
            <>
              <dt>Not serving because</dt>
              <dd>{SERVING_BLOCKED_REASON[slot.serving_blocked_reason] ?? slot.serving_blocked_reason}</dd>
            </>
          ) : null}
        </dl>

        {!retired ? (
          <div className="row" style={{ gap: "var(--space-2)" }}>
            {effectiveStatus === "paused" ? (
              <Button size="sm" variant="secondary" loading={statusCmd.isPending} onClick={() => statusCmd.mutate("resume")}>
                Resume
              </Button>
            ) : (
              <Button size="sm" variant="secondary" loading={statusCmd.isPending} onClick={() => statusCmd.mutate("pause")}>
                Pause
              </Button>
            )}
            <Button size="sm" variant="danger" loading={statusCmd.isPending} onClick={() => statusCmd.mutate("retire")}>
              Retire (permanent)
            </Button>
            {slot.pending_version ? (
              <Button size="sm" variant="ghost" loading={withdrawCmd.isPending} onClick={() => withdrawCmd.mutate()}>
                Withdraw pending version
              </Button>
            ) : null}
          </div>
        ) : (
          <Banner tone="neutral" title="Retired">
            This slot is retired. Its tool name stays reserved and it can't be changed again.
          </Banner>
        )}
        {statusCmd.isError ? <CommandError error={statusCmd.error} /> : null}
        {withdrawCmd.isError ? <CommandError error={withdrawCmd.error} /> : null}

        {!retired ? (
          <form className="stack-sm staff-subform" onSubmit={submitVersion} noValidate>
            <h3 className="small">New version</h3>
            <p className="xs muted">A pricing-model change is reviewed before it goes live; a tool-name-only change goes live at once.</p>
            <RadioGroup<PricingModel | "">
              legend="New pricing model"
              name={`slot_pricing_${slot.slot_id}`}
              value={newModel}
              onChange={setNewModel}
              options={[
                { value: "", label: "No change" },
                { value: "cpm", label: "CPM" },
                { value: "cpe", label: "CPE" },
              ]}
            />
            <Input label="New tool name" optional mono value={newTool} onChange={(e) => setNewTool(e.target.value)} hint="Leave empty to keep the current tool name." />
            {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
            {versionCmd.isError ? <CommandError error={versionCmd.error} /> : null}
            {versionCmd.data ? (
              <Banner tone="success" live="polite" title={versionCmd.data.result === "activated" ? "Live" : "Submitted for review"} />
            ) : null}
            <div>
              <Button type="submit" variant="secondary" loading={versionCmd.isPending}>
                Create version
              </Button>
            </div>
          </form>
        ) : null}

        <ExclusionsCard scope="slot" id={slot.slot_id} title="This slot's exclusions" />
      </div>
    </Dialog>
  );
}

function ApiKeysCard({ site }: { site: SiteDetail }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const createIntent = useIntentKey();
  const revokeIntent = useIntentKey();
  const keys = useCursorList<ApiKeyList, ApiKeyRow>(["api-keys", site.site_id], (cursor) => ep.publisher.listApiKeys(site.site_id, cursor), (p) => p.api_keys);
  const [shown, setShown] = useState<CreateApiKeyResponse | null>(null);
  const [replayed, setReplayed] = useState<CreateApiKeyResponse | null>(null);
  const [ack, setAck] = useState(false);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [revoking, setRevoking] = useState<{ key: ApiKeyRow; reason: RevocationReason } | null>(null);
  const [revoked, setRevoked] = useState<RevokeApiKeyResponse | null>(null);
  const active = site.status === "active";

  function refresh() {
    void qc.invalidateQueries({ queryKey: ["api-keys", site.site_id] });
    void qc.invalidateQueries({ queryKey: ["site", site.site_id] });
  }

  const create = useMutation({
    // One key per "Create key" intent: a retry after a lost response
    // replays the first create (and gets no secret) instead of minting a
    // second key.
    mutationFn: () => ep.publisher.createApiKey(site.site_id, { idempotencyKey: createIntent.keyFor("create") }),
    onSuccess: (res) => {
      createIntent.reset();
      setConfirmCreate(false);
      setAck(false);
      if (res.api_key && res.secret_available !== false) setShown(res);
      else setReplayed(res);
      refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: (v: { key: ApiKeyRow; reason: RevocationReason }) =>
      ep.publisher.revokeApiKey(v.key.api_key_id, v.reason, { idempotencyKey: revokeIntent.keyFor(`${v.key.api_key_id}|${v.reason}`) }),
    onSuccess: (res) => {
      revokeIntent.reset();
      setRevoking(null);
      setRevoked(res);
      if (replayed?.api_key_id === res.api_key_id) setReplayed(null);
      refresh();
    },
  });

  const liveCount = site.live_api_key_count;
  const lastLiveKey = revoking && revoking.key.status === "live" && liveCount <= 1;
  return (
    <Card title="API keys" subtitle="The SDK uses a key to request ads for this site. The full key is shown once, when it's created.">
      <div className="stack">
        {!active ? <Banner tone="neutral">Live API keys can be created once the site is active (after domain verification).</Banner> : null}
        {replayed ? (
          <Banner tone="warning" title="This key's secret can't be shown again">
            <div className="stack-sm">
              <span>
                Key <code>{replayed.prefix}</code> was already created by an earlier attempt whose response didn't reach you. The secret is
                shown only once and isn't stored anywhere, so it can't be recovered. Revoke this key and create a new one.
              </span>
              <span>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    const row = keys.rows.find((k) => k.api_key_id === replayed.api_key_id);
                    setRevoking({
                      key: row ?? {
                        api_key_id: replayed.api_key_id,
                        site_id: replayed.site_id,
                        prefix: replayed.prefix,
                        environment: "live",
                        scopes: [],
                        status: "live",
                        created_at: replayed.created_at,
                        created_by: null,
                        expires_at: null,
                        revoked_at: null,
                        revocation_reason: null,
                        last_used_at: null,
                      },
                      reason: "rotation",
                    });
                  }}
                >
                  Revoke {replayed.prefix}
                </Button>
              </span>
            </div>
          </Banner>
        ) : null}
        {revoked ? (
          <Banner tone="success" live="polite" title={`Key ${revoked.prefix} revoked`}>
            {revoked.affected.site_ids.length
              ? `That was the site's last live key: the site and ${revoked.affected.slot_ids.length} slot(s) stop serving live ads until a new key is deployed.`
              : `${revoked.remaining_live_keys} live key(s) remain on this site.`}
          </Banner>
        ) : null}
        <CursorListView list={keys} loadingLabel="Loading API keys" empty={<EmptyState icon="key" title="No API keys yet" />}>
          {(rows) => (
            <Table
              caption="API keys for this site"
              captionHidden
              rows={rows}
              rowKey={(k) => k.api_key_id}
              columns={[
                { key: "prefix", header: "Key", cell: (k) => <code>{k.prefix}</code> },
                {
                  key: "status",
                  header: "Status",
                  cell: (k) =>
                    k.status === "live" ? (
                      <StatusPill tone="success">Live</StatusPill>
                    ) : k.status === "revoked" ? (
                      <StatusPill tone="neutral">
                        Revoked{k.revocation_reason === "compromised" ? " — compromised" : k.revocation_reason === "rotation" ? " — rotated" : ""}
                      </StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Expired</StatusPill>
                    ),
                },
                { key: "created", header: "Created", cell: (k) => formatLocal(k.created_at) },
                { key: "by", header: "Created by", cell: (k) => k.created_by?.email ?? (k.created_by ? <code>{k.created_by.user_id.slice(0, 8)}</code> : "Not recorded") },
                { key: "used", header: "Last used", cell: (k) => (k.last_used_at ? formatLocal(k.last_used_at) : "Not recorded") },
                {
                  key: "act",
                  header: "Actions",
                  cell: (k) =>
                    k.status === "live" ? (
                      <span className="row" style={{ gap: "var(--space-2)" }}>
                        <Button size="sm" variant="secondary" onClick={() => (revoke.reset(), setRevoking({ key: k, reason: "rotation" }))}>
                          Revoke
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => (revoke.reset(), setRevoking({ key: k, reason: "compromised" }))}>
                          Revoke — key compromised
                        </Button>
                      </span>
                    ) : (
                      k.revoked_at ? `Revoked ${formatLocal(k.revoked_at)}` : "—"
                    ),
                },
              ]}
            />
          )}
        </CursorListView>
        <p className="xs muted">Test keys, key scopes and last-used times aren't available yet (API-GAPS.md G-35).</p>
        {create.isError && !confirmCreate ? <CommandError error={create.error} /> : null}
        <div>
          <Button onClick={() => (create.reset(), setConfirmCreate(true))} disabled={!active}>
            Create API key
          </Button>
        </div>
      </div>

      <Dialog
        open={confirmCreate}
        onClose={() => setConfirmCreate(false)}
        dismissible={!create.isPending}
        title="Create a new API key?"
        description="Existing keys keep working. To rotate, create the new key, deploy it, then revoke the old one."
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmCreate(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button onClick={() => create.mutate()} loading={create.isPending}>
              Create key
            </Button>
          </>
        }
      >
        {create.isError ? <CommandError error={create.error} /> : null}
      </Dialog>

      <Dialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        dismissible={!revoke.isPending}
        alert
        title={revoking?.reason === "compromised" ? "Revoke this key as compromised?" : "Revoke this key?"}
        description={
          revoking?.reason === "compromised"
            ? "It stops working immediately, with no grace period. Use this if the key may have leaked."
            : "It stops working immediately. Make sure your deployment already uses its replacement."
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => setRevoking(null)} disabled={revoke.isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => revoking && revoke.mutate(revoking)} loading={revoke.isPending}>
              {revoking?.reason === "compromised" ? "Revoke now — compromised" : "Revoke key"}
            </Button>
          </>
        }
      >
        {revoking ? (
          <div className="stack-sm">
            <span>
              Key <code>{revoking.key.prefix}</code>
            </span>
            {lastLiveKey ? (
              <Banner tone="warning" title="This is the site's only live key">
                Revoking it stops live serving for {site.domain} and all {site.slot_count} of its slots until a new key is deployed.
              </Banner>
            ) : (
              <span className="small">Other live keys keep {site.domain} serving.</span>
            )}
            {revoke.isError ? <CommandError error={revoke.error} /> : null}
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={shown !== null}
        onClose={() => (ack ? setShown(null) : undefined)}
        dismissible={ack}
        alert
        title="Copy this key now — you won't be able to see it again"
        description="We store only a hash of it. If it's lost, create a new key and revoke this one."
        actions={
          <Button onClick={() => setShown(null)} disabled={!ack}>
            I've stored it — close
          </Button>
        }
      >
        {shown?.api_key ? (
          <div className="stack">
            <CopyField label="API key" value={shown.api_key} />
            <Checkbox label="I've copied this key to a safe place" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          </div>
        ) : null}
      </Dialog>
    </Card>
  );
}

export function SiteIntegrationPage() {
  const { siteId = "" } = useParams();
  return (
    <div className="stack-lg">
      <PageHeader
        title="SDK integration"
        back={{ to: `/publisher/sites/${siteId}`, label: "Site" }}
        description="Two integration points, checked separately: decorating tool results, and the post-send acknowledgement hook."
      />
      <Card title="Install">
        <div className="stack">
          <p>Install the VADS publisher SDK for your server's language:</p>
          <CopyField label="npm install command" value="npm install vads-publisher-sdk" />
          <CopyField label="pip install command" value="pip install vads-publisher-sdk" />
          <ol className="stack-sm" style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li>
              Call <code>vads.decorate()</code> on each tool result from a slot's tool, using this site's API key.
            </li>
            <li>
              Call <code>vads.acknowledge(result)</code> after the response is handed to the transport (the post-send hook), so the platform receives{" "}
              <code>transport_acknowledged</code>. Without it the site is CPM-ineligible and runs on CPE or house ads only.
            </li>
          </ol>
          <p className="xs muted">
            Package names and snippets follow the SDKs in this repository (sdk/typescript, sdk/python); see their READMEs for the
            exact API.
          </p>
        </div>
      </Card>
      <IntegrationStatusCard siteId={siteId} />
    </div>
  );
}

const POST_SEND_HOOK_LABEL: Record<string, string> = {
  detected: "Detected — acknowledgements are arriving",
  not_detected: "Not detected — CPM impressions are decided but never acknowledged",
  no_cpm_traffic: "No CPM traffic yet",
};

function IntegrationStatusCard({ siteId }: { siteId: string }) {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["integration", siteId], queryFn: () => ep.publisher.getSiteIntegration(siteId) });
  return (
    <Card title="Integration health" subtitle="A live report of the last 24 hours, from a reporting replica.">
      <QueryView query={q} loadingLabel="Loading integration health">
        {(s) => (
          <div className="stack">
            <p className="small muted">
              <code>{s.domain}</code> · window {s.window} starting {formatLocal(s.window_start)}
            </p>
            <div className="grid-auto">
              <div className="stat">
                <span className="stat-label">Requests</span>
                <span className="stat-value">{s.counts.requests}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Filled</span>
                <span className="stat-value">{s.counts.filled}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Fill rate</span>
                <span className="stat-value">{s.rates.fill_rate === null ? "—" : `${(s.rates.fill_rate * 100).toFixed(2)}%`}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Attach rate</span>
                <span className="stat-value">{s.rates.attach_rate === null ? "—" : `${(s.rates.attach_rate * 100).toFixed(2)}%`}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Ack rate (CPM)</span>
                <span className="stat-value">{s.rates.ack_rate === null ? "—" : `${(s.rates.ack_rate * 100).toFixed(2)}%`}</span>
              </div>
            </div>
            <dl className="definition-list">
              <dt>decorate() last seen</dt>
              <dd>{s.decorate_last_seen_at ? formatLocal(s.decorate_last_seen_at) : "Never, in the last 30 days"}</dd>
              <dt>Post-send hook (acknowledge)</dt>
              <dd>
                <StatusPill tone={s.post_send_hook.status === "detected" ? "success" : s.post_send_hook.status === "not_detected" ? "warning" : "neutral"}>
                  {POST_SEND_HOOK_LABEL[s.post_send_hook.status] ?? s.post_send_hook.status}
                </StatusPill>
              </dd>
              <dt>Decided / no-fill / rejected (24h)</dt>
              <dd>
                {s.counts.decided} / {s.counts.no_fill} / {s.counts.rejected}
              </dd>
              <dt>Attached / acknowledged / redeemed (24h)</dt>
              <dd>
                {s.counts.attached} / {s.counts.acknowledged} / {s.counts.redeemed}
              </dd>
            </dl>
            {s.last_error ? (
              <Banner tone="warning" title={`Last error: ${s.last_error.error_class}`}>
                {formatLocal(s.last_error.at)}
              </Banner>
            ) : (
              <p className="small muted">No integration errors in the last 24 hours.</p>
            )}
            {s.errors.length ? (
              <Table
                caption="Error classes (24h)"
                rows={s.errors}
                rowKey={(e) => e.error_class}
                columns={[
                  { key: "class", header: "Error class", cell: (e) => <code>{e.error_class}</code> },
                  { key: "count", header: "Count", numeric: true, cell: (e) => e.count },
                  { key: "last", header: "Last seen", cell: (e) => formatLocal(e.last_at) },
                ]}
              />
            ) : null}
            <NotAvailable what="the sampling channel, verification window, CPM eligibility enforcement and CPE mode" endpoint="GET /v1/sites/{id}/integration" gapId="G-37">
              {s.not_built.join(", ")} — the response says these plainly rather than guessing.
            </NotAvailable>
          </div>
        )}
      </QueryView>
      <p className="xs muted" style={{ marginTop: "var(--space-3)" }}>
        <Link to={`/publisher/sites/${siteId}`}>Back to site settings</Link>
      </p>
    </Card>
  );
}
